import { api } from "./client";
import type { ProcessingAvailability, ProcessingJob } from "./types";

/**
 * The first-ingest upload path: a whole product folder at once.
 *
 * This is a different operation from replacing one view on an existing product
 * (see api/images.ts). Here nothing exists yet — the batch is uploaded, the
 * worker builds the derivatives, and only then does a product get created from
 * the result. Which is why the order in useGlassesImport cannot be rearranged:
 * POST /admin/products takes an upload batch the worker has already finished.
 */

/** One file's signed slot in the batch.
 *
 * Handled as a secret exactly like InitiatedImageUpload: held only for the
 * lifetime of the import, never put in the React Query cache, never persisted,
 * never logged and never rendered. */
export interface SignedUpload {
  storage_key: string;
  upload_url: string;
  method: string;
  headers: Record<string, string>;
  expires_in: number;
}

export interface InitiatedUpload {
  upload_batch_id: string;
  files: SignedUpload[];
}

export interface CompletedUpload {
  upload_batch_id: string;
  processing_job_id: string;
  status: string;
  queued: boolean;
  queue_error: string | null;
}

export interface UploadBatch {
  id: string;
  status: string;
  original_filename_or_folder: string;
  error_summary: string | null;
  processing_job_ids: string[];
}

export const uploadsApi = {
  /** Opens a batch and returns one signed slot per file, in the order sent. */
  initiate: (body: {
    original_filename_or_folder: string;
    relative_paths: string[];
  }) => api.post<InitiatedUpload>("/admin/uploads/initiate", body),

  /** Tells the backend every object is in place, which queues the worker. */
  complete: (uploadBatchId: string) =>
    api.post<CompletedUpload>(`/admin/uploads/${uploadBatchId}/complete`),

  batch: (uploadBatchId: string, signal?: AbortSignal) =>
    api.get<UploadBatch>(`/admin/uploads/${uploadBatchId}`, undefined, signal),

  job: (jobId: string, signal?: AbortSignal) =>
    api.get<ProcessingJob>(`/admin/processing-jobs/${jobId}`, undefined, signal),

  /** Whether a worker is available to build images right now. Consulted while a
   *  job sits queued, so a genuinely-progressing wait is told apart from one
   *  that has stalled because nothing is running. */
  availability: (signal?: AbortSignal) =>
    api.get<ProcessingAvailability>(
      "/admin/processing/availability", undefined, signal),
};
